import { cn } from '@brew/ui/cn'
import type { ReactNode } from 'react'
import { MK_LABEL, MK_SMALL, MK_SURFACE_RADIUS } from './home-layout'

/**
 * One tile's box inside a `TilePanel` (links add their own hover). Each
 * tile draws the hairline on its right and bottom; the panel clips the ones
 * on its outer edge, and a short last row leaves panel-colored space.
 */
export const TILE =
  'relative flex min-h-44 min-w-0 flex-col gap-6 bg-mk-tile p-6 shadow-[1px_0_0_var(--mk-line),0_1px_0_var(--mk-line)]'

const COLUMNS = {
  1: '',
  2: 'sm:grid-cols-2',
  3: 'sm:grid-cols-2 lg:grid-cols-3',
  4: 'sm:grid-cols-2 lg:grid-cols-4',
} as const

/**
 * The home integrations panel's language for a set of tiles: a 24px-radius
 * panel with an 8px inset, its tiles ruled apart by hairlines.
 */
export function TilePanel({
  columns = 3,
  className,
  children,
}: {
  columns?: keyof typeof COLUMNS
  className?: string
  children: ReactNode
}) {
  return (
    <div
      className={cn(
        'w-full border border-mk-line bg-mk-tile p-2',
        MK_SURFACE_RADIUS,
        className
      )}
    >
      <div
        className={cn(
          'grid overflow-clip rounded-2xl bg-mk-tile',
          COLUMNS[columns]
        )}
      >
        {children}
      </div>
    </div>
  )
}

/** A tile's title and its line under it. */
export function TileCopy({
  title,
  children,
  className,
}: {
  title: ReactNode
  children: ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <span className={cn(MK_LABEL, 'text-mk-ink')}>{title}</span>
      <span className={cn(MK_SMALL, 'text-mk-body')}>{children}</span>
    </div>
  )
}

/** A numbered step ("01 · Design") in a `TilePanel`. */
export function StepTile({
  step,
  title,
  children,
}: {
  step: string
  title: string
  children: ReactNode
}) {
  return (
    <div className={TILE}>
      <span className="font-medium font-mk-body text-[13px] text-mk-accent-text tabular-nums leading-[18px]">
        {step}
      </span>
      <TileCopy title={title}>{children}</TileCopy>
    </div>
  )
}
